/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import { EventBusModule } from "@vlinder-web/event-bus-module-react";
import { FSMFactoryModule } from "@vlinder-web/state-machine-factory-module-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { WalletconnectModule } from "@vlinder-web/walletconnect-module-react";
//import { WalletconnectModule } from "../modules/WalletConnect";
import { BaseApplication } from "./base/baseApplication";
import { UnitTest } from "./unit.test";

import {
    TrafficLightService,
    DeviceInfoService,
    DummyRestService,
    DummyGraphQLService,
    CollectiblesAssetService,
    SportsAssetService,
    EntertainmentAssetService,
    AssetDetailService,
    ContactUsRestService,
    WalletApiService,
    WalletConnectClientService,
    FiatDetailService,
    ProfileService,
    AuthService,
    PdfService,
    HeaderNavService,
    UserDetailService,
    UserProfileService,
    TragProvenanceService,
    MyWalletService,
    Category4AssetService,
    Category5AssetService
} from "../services";

import { StorageModule } from "@vlinder-web/storage-module-react";

import { DEFAULT_APP_CONFIG } from "../config/default.config";
import { DZIService } from "../services/dzi.service";

export class VApplication extends BaseApplication {
    private static instance: VApplication;

    private constructor(options: any = {}) {
        super(options);

        this.setupBindings();

        //Modules
        this.initModules();

        //Services
        this.initServices();
    }

    static getInstance(options: any = {}) {
        if (!this.instance) {
            this.instance = new VApplication(options);
        }
        return this.instance;
    }

    // @ts-ignore
    async start() {
        await this.initRunLevel();
        await this.unitTest();
    }

    setupBindings() {
        //------------->>>>>--------------//
        this.configure("services.DummyRestService", {
            baseURL: DEFAULT_APP_CONFIG?.servers.rest.restServer.baseURL,
        });
        this.configure("services.ContactUsRestService", {
            baseURL: DEFAULT_APP_CONFIG?.servers.rest.restServer.baseURL,
        });

        this.configure("services.WalletconnectModule", {
            bridge: DEFAULT_APP_CONFIG.walletConnect?.relayURL,
        });
        //------------->>>>>--------------//
    }

    async initRunLevel() {
        //------------->>>>>--------------//
        const graphqlModule = this.getSync(
            "services.GraphqlServerModule"
        ) as GraphqlServerModule;
        if (graphqlModule) {
            type TClient = typeof DEFAULT_APP_CONFIG.servers.graphql[0];
            DEFAULT_APP_CONFIG.servers.graphql.forEach(
                async (client: TClient) => {
                    await graphqlModule.createClient(client.name, {
                        url: client.url,
                        getContext: this.getGraphQLContext.bind(this),
                    });
                }
            );
        }
        //------------->>>>>--------------//

        //------------->>>>>--------------//
        const wcClientService = this.getSync(
            "services.WalletConnectClientService"
        ) as WalletConnectClientService;
        await wcClientService.init();
        //------------->>>>>--------------//

        //------------->>>>>--------------//
        // const wcModule = this.getSync(
        //     "services.WalletconnectModule"
        // ) as WalletconnectModule;
        // await wcModule.init();
        //------------->>>>>--------------//

        const storageService = this.getSync(
            "services.StorageModule"
        ) as StorageModule;
        await storageService.init();
    }

    async unitTest() {
        const ut = new UnitTest(VApplication);
        ut.run();
    }

    initModules() {
        this.service("services.FSMFactoryModule", FSMFactoryModule);
        this.service("services.EventBusModule", EventBusModule);
        this.service("services.GraphqlServerModule", GraphqlServerModule);
        this.service("services.WalletconnectModule", WalletconnectModule);
        this.service("services.StorageModule", StorageModule);
    }

    initServices() {
        this.service("services.AuthService", AuthService);
        this.service("services.TrafficLightService", TrafficLightService);
        this.service("services.DummyRestService", DummyRestService);
        this.service("services.DeviceInfoService", DeviceInfoService);
        this.service("services.DummyGraphQLService", DummyGraphQLService);
        this.service(
            "services.CollectiblesAssetService",
            CollectiblesAssetService
        );
        this.service(
            "services.Category4AssetService",
            Category4AssetService
        );
        this.service(
            "services.Category5AssetService",
            Category5AssetService
        );    
        this.service("services.UserDetailService", UserDetailService);
        this.service("services.SportsAssetService", SportsAssetService);
        this.service(
            "services.EntertainmentAssetService",
            EntertainmentAssetService
        );
        this.service("services.ContactUsRestService", ContactUsRestService);
        this.service("services.AssetDetailService", AssetDetailService);
        this.service(
            "services.WalletConnectClientService",
            WalletConnectClientService
        );
        this.service("services.WalletApiService", WalletApiService);
        this.service("services.FiatDetailService", FiatDetailService);
        this.service("services.ProfileService", ProfileService);
        this.service("services.PdfService", PdfService);
        this.service("services.HeaderNavService", HeaderNavService);
        this.service("services.UserProfileService", UserProfileService);
        this.service("services.TragProvenanceService", TragProvenanceService);
        this.service("services.MyWalletService", MyWalletService);
        this.service("services.DZIService", DZIService);
    }

    private getGraphQLContext() {
        const deviceInfoService = this.getSync(
            "services.DeviceInfoService"
        ) as DeviceInfoService;
        //FIXME:
        return {
            headers: {
                authentication: "Iam your token",
                headers: {
                    ...{ ...deviceInfoService?.getDeviceInfo() },
                    ...{ ...deviceInfoService?.getAppInfo() },
                },
            },
        };
    }
}
